import { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { getTheme } from '../config/themes';

// ════════════════════════════════════════════════════════════════════
//  HolidayEffects — Optimized
//  ✓ Єдиний RAF loop для всіх частинок
//  ✓ GPU translate3d + will-change (без layout thrashing)
//  ✓ Пул DOM елементів (без GC pressure)
//  ✓ Spawn через RAF замість setTimeout
//  ✓ DocumentFragment для footer
//  ✓ Кешований viewport size
// ════════════════════════════════════════════════════════════════════

function injectBaseCSS() {
  if (document.getElementById('holiday-effects-css')) return;
  const s = document.createElement('style');
  s.id = 'holiday-effects-css';
  s.textContent = `
    .hfx { position:fixed; pointer-events:none; user-select:none; will-change:transform,opacity; top:0; left:0; }
    @keyframes hfx-float-0 { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-12px) rotate(15deg)} }
    @keyframes hfx-float-1 { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-8px) rotate(-10deg)} }
    @keyframes hfx-float-2 { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-16px) rotate(20deg)} }
    .hfx-footer { position:absolute; pointer-events:none; user-select:none; z-index:0; }
  `;
  document.head.appendChild(s);
}

// ── Particle pool ────────────────────────────────────────────────────
const _pool = [];
const _MAX_ACTIVE = 30; // Hard limit — prevents iOS GPU crash
const _getEl = () => {
  if (_active.length >= _MAX_ACTIVE) return null; // iOS GPU limit
  if (_pool.length) { const e = _pool.pop(); e.className = 'hfx'; return e; }
  const e = document.createElement('div'); e.className = 'hfx'; return e;
};
const _releaseEl = (el) => {
  el.style.cssText = ''; el.textContent = '';
  if (_pool.length < 50) _pool.push(el); else el.remove();
};

// ── Single RAF loop ──────────────────────────────────────────────────
const _active = [];
let _raf = null;

const _tick = (now) => {
  _raf = null;
  for (let i = _active.length - 1; i >= 0; i--) {
    if (_active[i](now)) {
      _active.splice(i, 1);
    }
  }
  if (_active.length) _raf = requestAnimationFrame(_tick);
};

const _add = (fn) => {
  _active.push(fn);
  if (!_raf) _raf = requestAnimationFrame(_tick);
};

const _clearAll = () => {
  if (_raf) { cancelAnimationFrame(_raf); _raf = null; }
  _active.length = 0;
  document.querySelectorAll('.hfx').forEach(el => _releaseEl(el));
};

// ── Viewport cache ───────────────────────────────────────────────────
// ✅ FIX: safe initialization — window may not be ready at module parse time (Safari iOS crash)
let _vw = (typeof window !== 'undefined') ? window.innerWidth : 375;
let _vh = (typeof window !== 'undefined') ? window.innerHeight : 812;
if (typeof window !== 'undefined') {
  window.addEventListener('resize', () => { _vw = window.innerWidth; _vh = window.innerHeight; }, { passive: true });
}

// ── FALLING ──────────────────────────────────────────────────────────
function startFalling(cfg) {
  let stopped = false;
  let nextSpawn = 0;

  const spawnLoop = (now) => {
    if (stopped) return;
    if (now >= nextSpawn) {
      const el       = _getEl();
      if (!el) { nextSpawn = now + cfg.rate; requestAnimationFrame(spawnLoop); return; } // iOS limit
      const item     = cfg.items[Math.floor(Math.random() * cfg.items.length)];
      const size     = cfg.minSize + Math.random() * (cfg.maxSize - cfg.minSize);
      const duration = cfg.minSpeed + Math.random() * (cfg.maxSpeed - cfg.minSpeed);
      const drift    = (Math.random() - 0.5) * cfg.drift;
      const rot      = Math.random() * 720 - 360;
      const startX   = Math.random() * (_vw + 40) - 20;
      const totalH   = _vh + 100;

      el.textContent = item;
      el.style.fontSize = size + 'px';
      el.style.zIndex = '1';
      el.style.transform = `translate3d(${startX}px,-50px,0)`;
      el.style.opacity = '0';
      document.body.appendChild(el);

      const t0 = performance.now();
      _add((now) => {
        if (stopped) { el.remove(); return true; }
        const t = (now - t0) / duration;
        if (t >= 1) { _releaseEl(el); return true; }
        const fade = (t < 0.08 ? t / 0.08 : 1) * (t > 0.88 ? (1 - t) / 0.12 : 1);
        el.style.opacity = cfg.opacity * fade;
        el.style.transform = `translate3d(${startX + Math.sin(t * Math.PI * 3) * drift}px,${t * totalH - 50}px,0) rotate(${rot * t}deg)`;
        return false;
      });

      nextSpawn = now + cfg.rate * (0.7 + Math.random() * 0.6);
    }
    requestAnimationFrame(spawnLoop);
  };

  requestAnimationFrame(spawnLoop);
  return () => { stopped = true; };
}

// ── CLICK burst ──────────────────────────────────────────────────────
function spawnClickBurst(x, y, cfg) {
  for (let i = 0; i < cfg.count; i++) {
    const el      = _getEl();
    if (!el) return; // iOS limit
    const angle   = (i / cfg.count) * Math.PI * 2 + Math.random() * 0.4;
    const dist    = cfg.distance * (0.6 + Math.random() * 0.8);
    const isEmoji = Math.random() < cfg.emojiChance;
    const color   = cfg.colors[Math.floor(Math.random() * cfg.colors.length)];
    const size    = 6 + Math.random() * 7;
    const tx      = Math.cos(angle) * dist;
    const ty      = Math.sin(angle) * dist;

    el.textContent = isEmoji ? cfg.items[Math.floor(Math.random() * cfg.items.length)] : '';
    el.style.fontSize = (isEmoji ? 14 + Math.random() * 8 : size) + 'px';
    el.style.zIndex = '9999';
    if (!isEmoji) { el.style.width = el.style.height = size + 'px'; el.style.borderRadius = '50%'; el.style.background = color; }
    el.style.transform = `translate3d(${x}px,${y}px,0)`;
    el.style.opacity = '1';
    document.body.appendChild(el);

    const t0 = performance.now();
    const dur = cfg.duration, grav = cfg.gravity;
    _add((now) => {
      const t = (now - t0) / dur;
      if (t >= 1) { _releaseEl(el); return true; }
      el.style.opacity = 1 - t * t;
      el.style.transform = `translate3d(${x + tx * t}px,${y + ty * t + t * t * grav}px,0) rotate(${t * 360}deg)`;
      return false;
    });
  }
}

// ── SCROLL sparkle ───────────────────────────────────────────────────
function spawnScrollParticle(cfg) {
  const el    = _getEl();
  if (!el) return; // iOS limit
  const color = cfg.colors[Math.floor(Math.random() * cfg.colors.length)];
  const size  = cfg.size.min + Math.random() * (cfg.size.max - cfg.size.min);
  const dur   = cfg.duration.min + Math.random() * (cfg.duration.max - cfg.duration.min);
  const x     = Math.random() * _vw;
  const y     = Math.random() * _vh;
  const rise  = -(cfg.rise + Math.random() * cfg.rise);

  el.style.width = el.style.height = size + 'px';
  el.style.borderRadius = '50%';
  el.style.background = color;
  el.style.zIndex = '9998';
  if (cfg.glow) el.style.boxShadow = `0 0 ${size * 2}px ${color}`;
  el.style.transform = `translate3d(${x}px,${y}px,0)`;
  el.style.opacity = '0.85';
  document.body.appendChild(el);

  const t0 = performance.now();
  _add((now) => {
    const t = (now - t0) / dur;
    if (t >= 1) { _releaseEl(el); return true; }
    el.style.opacity = (1 - t * t) * 0.85;
    el.style.transform = `translate3d(${x}px,${y + rise * t}px,0) scale(${1 - t * 0.5})`;
    return false;
  });
}

// ── FOOTER decorations ───────────────────────────────────────────────
function spawnFooterDecorations(cfg) {
  const footer = document.querySelector('.site-footer');
  if (!footer || footer.querySelector('.hfx-footer')) return;
  footer.style.position = 'relative';
  footer.style.overflow = 'hidden';
  const frag = document.createDocumentFragment();
  for (let i = 0; i < cfg.count; i++) {
    const el   = document.createElement('div');
    el.className = 'hfx-footer';
    const size    = cfg.size.min + Math.random() * (cfg.size.max - cfg.size.min);
    const opacity = cfg.opacity.min + Math.random() * (cfg.opacity.max - cfg.opacity.min);
    const anim    = cfg.animations[i % cfg.animations.length];
    el.textContent = cfg.items[Math.floor(Math.random() * cfg.items.length)];
    el.style.cssText = `font-size:${size}px;left:${Math.random()*100}%;bottom:${5+Math.random()*85}%;opacity:${opacity};animation:${anim} ${3+Math.random()*4}s ease-in-out infinite ${Math.random()*3}s;`;
    frag.appendChild(el);
  }
  footer.appendChild(frag);
}

const removeFooterDecorations = () =>
  document.querySelectorAll('.hfx-footer').forEach(el => el.remove());

// ── Main component ───────────────────────────────────────────────────
export default function HolidayEffects() {
  const { theme }      = useTheme();
  const stopFalling    = useRef(null);
  const scrollThrottle = useRef(0);
  const effects        = getTheme(theme).effects;
  const [effectsOverride, setEffectsOverride] = useState(() => {
    const localOff    = (() => { try { return localStorage.getItem('bv_effects_off') === 'true'; } catch { return false; } })();
    const settingsOff = window._bvS?.holidayEffects === 'FALSE' ||
                        window._bvS?.holidayEffects === false ||
                        window._bvS?.holidayEffects === 'false';
    return !localOff && !settingsOff;
  });

  // effectsOverride вже враховує і localStorage і _bvS.holidayEffects
  const effectsEnabled = effectsOverride;

  // Слухаємо toggle від ThemeSelector і зміну Settings
  useEffect(() => {
    const handler = () => {
      // Перечитуємо обидва джерела: localStorage і window._bvS
      const localOff    = (() => { try { return localStorage.getItem('bv_effects_off') === 'true'; } catch { return false; } })();
      const settingsOff = window._bvS?.holidayEffects === 'FALSE' ||
                          window._bvS?.holidayEffects === false ||
                          window._bvS?.holidayEffects === 'false';
      setEffectsOverride(!localOff && !settingsOff);
    };
    window.addEventListener('bv_effects_changed', handler);
    window.addEventListener('bv_settings_updated', handler);
    return () => {
      window.removeEventListener('bv_effects_changed', handler);
      window.removeEventListener('bv_settings_updated', handler);
    };
  }, []);

  const handleClick = useCallback((e) => {
    if (!effectsEnabled || !effects?.click?.enabled) return;
    spawnClickBurst(e.clientX, e.clientY, effects.click);
  }, [effects, effectsEnabled]);

  const handleScroll = useCallback(() => {
    if (!effectsEnabled || !effects?.scroll?.enabled) return;
    const now = Date.now();
    if (now - scrollThrottle.current < effects.scroll.throttle) return;
    scrollThrottle.current = now;
    spawnScrollParticle(effects.scroll);
  }, [effects, effectsEnabled]);

  useEffect(() => {
    injectBaseCSS();
    if (stopFalling.current) { stopFalling.current(); stopFalling.current = null; }
    _clearAll();
    removeFooterDecorations();
    if (!effects || !effectsEnabled) return;
    if (effects.falling?.enabled) stopFalling.current = startFalling(effects.falling);
    let ft;
    if (effects.footer?.enabled) ft = setTimeout(() => spawnFooterDecorations(effects.footer), 600);

    // Safari BFCache: зупиняємо RAF коли сторінка прихована, відновлюємо при поверненні
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        _clearAll();
        if (stopFalling.current) { stopFalling.current(); stopFalling.current = null; }
      } else if (document.visibilityState === 'visible') {
        if (effects.falling?.enabled) stopFalling.current = startFalling(effects.falling);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearTimeout(ft);
      document.removeEventListener('visibilitychange', onVisibility);
      if (stopFalling.current) { stopFalling.current(); stopFalling.current = null; }
      _clearAll();
      removeFooterDecorations();
    };
  }, [theme, effects, effectsEnabled]);

  useEffect(() => {
    document.addEventListener('click', handleClick);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      document.removeEventListener('click', handleClick);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleClick, handleScroll]);

  return null;
}
